// File d'attente de triage des mails Outlook non rattachés automatiquement (EPIC Outlook).
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useOutlookQueue, useAssignOutlookMessage } from '../../hooks/queries/useOutlookQueue';
import { useDossiers } from '../../hooks/queries/useDossiers';
import type { OutlookQueueItem } from '../../services/outlook/outlookConnectionService';

import { formatAppDate } from '../../lib/dateFormat';
export function OutlookTriageQueue() {
  const { data: items, isLoading } = useOutlookQueue();
  const { data: dossiers } = useDossiers();
  const assign = useAssignOutlookMessage();
  const [selected, setSelected] = useState<Record<string, string>>({});

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 text-sm text-neutral-500">
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
        Chargement…
      </p>
    );
  }

  if (!items || items.length === 0) {
    return <p className="text-sm text-neutral-500">Aucun mail à trier.</p>;
  }

  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <TriageRow
          key={item.id}
          item={item}
          dossiers={dossiers ?? []}
          selected={selected[item.id] ?? ''}
          onSelect={(dossierId) => setSelected((s) => ({ ...s, [item.id]: dossierId }))}
          onAssign={() =>
            assign.mutate({ processedMessageId: item.id, dossierId: selected[item.id] ?? '' })
          }
          isPending={assign.isPending}
        />
      ))}
    </ul>
  );
}

function TriageRow({
  item,
  dossiers,
  selected,
  onSelect,
  onAssign,
  isPending,
}: {
  item: OutlookQueueItem;
  dossiers: { id: string; title: string; number: string }[];
  selected: string;
  onSelect: (dossierId: string) => void;
  onAssign: () => void;
  isPending: boolean;
}) {
  const options =
    item.status === 'queued' && item.candidate_dossier_ids
      ? dossiers.filter((d) => item.candidate_dossier_ids!.includes(d.id))
      : dossiers;

  // Un 'queued' peut n'avoir qu'UN seul dossier candidat (match certain, mais
  // désormais proposé et non plus importé automatiquement) — le libellé ne doit
  // pas mentir en annonçant « plusieurs dossiers ».
  const singleCandidate =
    item.status === 'queued' && (item.candidate_dossier_ids?.length ?? 0) === 1;
  const hint =
    item.status === 'unmatched'
      ? ' · expéditeur inconnu'
      : singleCandidate
        ? ' · dossier suggéré'
        : ' · plusieurs dossiers';

  return (
    <li className="flex items-center justify-between gap-2 rounded border border-neutral-200 p-2">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-neutral-700">
          {item.subject || '(sans objet)'}
        </p>
        <p className="truncate text-xs text-neutral-500">
          {item.from_name || item.from_email} ·{' '}
          {formatAppDate(item.internal_date)}
          {hint}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <select
          className="rounded border border-neutral-200 px-2 py-1 text-xs text-neutral-700"
          value={selected}
          onChange={(e) => onSelect(e.target.value)}
        >
          <option value="">Choisir un dossier…</option>
          {options.map((d) => (
            <option key={d.id} value={d.id}>
              {d.number} — {d.title}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="rounded bg-cime-primary px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
          disabled={!selected || isPending}
          onClick={onAssign}
        >
          Rattacher
        </button>
      </div>
    </li>
  );
}
